import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Autocomplete, Typography, GlobalStyles, Link, Avatar } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Calendar from "examples/Calendar";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import { formatDate, formatTimeForDisplay, formatLocalDateTimeInput, formatLocalDateInput, toLocalDateTimeApi, normalizeTimeToHHMMSS, normalizeTimeToHHMM } from "utils/DateTimeConversion";
import Card from "@mui/material/Card";
import colors from "assets/theme/base/colors";
import { styled, useTheme } from "@mui/material/styles";
import { frontendLogger } from '../../utils/logger';
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import PageHeader from "components/v2/PageHeader";
import { useActiveApp } from "shell-v2/apps";
import { v2 as t, v2Tones } from "styles/tokens/v2";


const relatedToOptions = [
  { value: "account", label: "Account" },
  { value: "contact", label: "Contact" },
  { value: "lead", label: "Lead" },
  { value: "deal", label: "Deal" },
  { value: "case", label: "Case" },
  { value: "order", label: "Order" },
  { value: "quote", label: "Quotation" },
];

const EMPTY_EVENT_ERRORS = { title: "", start: "", starttime: "", endtime: "" };

const scrollToFirstInvalid = (container) => {
  setTimeout(() => {
    const el = container?.querySelector('[aria-invalid="true"]');
    if (!container || !el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.scrollIntoView({ block: "center" });
      el.focus({ preventScroll: true });
      return;
    }

    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const target =
        container.scrollTop + (eRect.top - cRect.top) - container.clientHeight / 2 + eRect.height / 2;

    const start = container.scrollTop;
    const distance = Math.max(0, target) - start;
    const duration = 400;
    const startTime = performance.now();
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      container.scrollTop = start + distance * ease(p);
      if (p < 1) requestAnimationFrame(step);
      else el.focus({ preventScroll: true });
    };
    requestAnimationFrame(step);
  }, 50);
};


const TaskView = ({ event, onDelete, onClose, onUpdate, onNotify }) => {
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [users, setUsers] = useState([]);
  const [relatedEntities, setRelatedEntities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  const [dueDateError, setDueDateError] = useState("");
  const [timeErrors, setTimeErrors] = useState({ starttime: "", endtime: "" });


  const panelRef = useRef(null);



  const fieldStyle = {
    label: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#344767",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      marginBottom: "4px"
    },
    value: {
      fontSize: "0.875rem",
      color: "#67748e",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      marginBottom: "16px"
    },
    editField: {
      "& .MuiOutlinedInput-root": {
        fontSize: "0.875rem",
        color: "#67748e",
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.875rem",
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
      }
    }
  };

  useEffect(() => {
    if (isEditing) {
      setDueDateError("");
      setTimeErrors({ starttime: "", endtime: "" });
      setEditedData({
        id: event.id,
        type: event.extendedProps.type,
        title: event.title || event.extendedProps.title,
        description: event.extendedProps.description,
        due_date: event.extendedProps.due_date,
        starttime: event.extendedProps.starttime,
        endtime: event.extendedProps.endtime,
        assignee_id: event.extendedProps.assignee_id,
        related_to: {
          type: event.extendedProps.parent_table?.replace(/s$/, ""),
          id: event.extendedProps.parent_table_id
        },
        status: event.extendedProps.status
      });
      fetchUsers();
      if (event.extendedProps.parent_table) {
        fetchRelatedEntities(event.extendedProps.parent_table.replace(/s$/, ""));
      }
    }
  }, [isEditing, event]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users/");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchRelatedEntities = async (type) => {
    try {
      let response;
      if (type === "quote") {
        response = await axiosInstance.get('/quotations/'); // Fetch quotations
      } else {
        response = await axiosInstance.get(`/${type}s/`); // Fetch other entities
      }

      setRelatedEntities((prev) => ({
        ...prev,
        [type]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching ${type}s:`, error);
      showSnackbar(`Failed to fetch ${type}s`, "error");
    }
  };

  const handleEntityClick = () => {
    const parentTable = event.extendedProps.parent_table.replace(/s$/, "");
    const entityId = event.extendedProps.parent_table_id;

    const routeMap = {
      lead: "/leads/",
      deal: "/deals/",
      contact: "/contacts/",
      account: "/accounts/",
      case: "/cases/",
      order: "/orders/"
    };

    const baseRoute = routeMap[parentTable];
    if (baseRoute) {
      navigate(`${baseRoute}${entityId}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRelatedToChange = async (e) => {
    const { name, value } = e.target;
    if (name === "type") {
      setEditedData((prev) => ({
        ...prev,
        related_to: { type: value, id: "" }
      }));
      if (value) {
        await fetchRelatedEntities(value);
      }
    }
  };

  const validateMeetingTimes = () => {
    if (editedData.type !== "Meeting") return true;

    const start = (editedData.starttime || "").slice(0, 5);
    const end = (editedData.endtime || "").slice(0, 5);
    const errors = { starttime: "", endtime: "" };

    if (!start) errors.starttime = "Start time is required";
    if (!end) errors.endtime = "End time is required";
    if (start && end && end <= start) errors.endtime = "End time must be after start time";

    setTimeErrors(errors);
    return !errors.starttime && !errors.endtime;
  };

  const handleSave = async () => {
    if (!editedData.title?.trim()) {
      showSnackbar("Title is required", "error");
      scrollToFirstInvalid(panelRef.current)
      return;
    }

    if (!editedData.due_date) {
      setDueDateError("Due date is required");
      scrollToFirstInvalid(panelRef.current)
      return;
    }

    if (!validateMeetingTimes()) {
      scrollToFirstInvalid(panelRef.current)
      return;
    }

    setUpdateConfirmOpen(true);
  };

  const handleConfirmUpdate = async () => {
    try {
      setIsLoading(true);

      const originalData = {
        type: event.extendedProps.type,
        title: event.title || event.extendedProps.title,
        description: event.extendedProps.description,
        due_date: event.extendedProps.due_date,
        assigned_to: event.extendedProps.assignee_id,
        parent_table: event.extendedProps.parent_table,
        parent_table_id: event.extendedProps.parent_table_id,
        starttime: event.extendedProps.starttime,
        endtime: event.extendedProps.endtime,
        status: event.extendedProps.status
      };

      // Date validation for status
      const selectedDate = new Date(editedData.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for proper comparison

      // Validate status based on date
      if (selectedDate < today && editedData.status === "Scheduled") {
        showSnackbar("Cannot set a past date task as Scheduled", "error");
        setIsLoading(false);
        return;
      }

      const updateData = {};

      if (editedData.type !== originalData.type) {
        updateData.type = editedData.type;
      }
      if (editedData.title !== originalData.title) {
        updateData.title = editedData.title;
      }
      if (editedData.description !== originalData.description) {
        updateData.description = editedData.description;
      }
      if (editedData.due_date !== originalData.due_date) {
        updateData.due_date = editedData.due_date;
      }
      if (editedData.assignee_id !== originalData.assigned_to) {
        updateData.assigned_to = editedData.assignee_id;
      }
      if (`${editedData.related_to.type}s` !== originalData.parent_table) {
        updateData.parent_table = `${editedData.related_to.type}s`;
      }
      if (editedData.related_to.id !== originalData.parent_table_id) {
        updateData.parent_table_id = editedData.related_to.id;
      }

      const isMeeting = editedData.type === "Meeting";
      const newStart = isMeeting ? (editedData.starttime || null) : null;
      const newEnd = isMeeting ? (editedData.endtime || null) : null;

      if (newStart !== (originalData.starttime || null)) {
        updateData.starttime = newStart;
      }
      if (newEnd !== (originalData.endtime || null)) {
        updateData.endtime = newEnd;
      }

      if (editedData.status !== originalData.status) {
        updateData.status = editedData.status;
      }

      if (Object.keys(updateData).length > 0) {
        const response = await axiosInstance.patch(`/activities/${event.id}`, updateData);

        setUpdateConfirmOpen(false);
        setIsEditing(false);
        onNotify?.("Task updated successfully");
        onUpdate(response.data);
      } else {
        setUpdateConfirmOpen(false);
        setIsEditing(false);
        onNotify?.("No changes to update", "info");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      showSnackbar("Failed to update task", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
    setDeleteConfirmOpen(false);
  };

  const renderField = (label, content, editComponent) => (
    <Box mb={2}>
      <Typography sx={fieldStyle.label}>{label}</Typography>
      {isEditing ? editComponent : (
        <Typography sx={fieldStyle.value}>{content}</Typography>
      )}
    </Box>
  );

  // Add handler function for navigation
  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/users/${userId}/profile`);
    }
  };

  return (
    <>
      <Box
        ref={panelRef}
        sx={{
          p: 3,
          height: '100%',
          borderLeft: "1px solid #eee",
          position: "relative",
          backgroundColor: "white",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "3px"
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.05)"
          }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDTypography
            variant="h6"
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#344767",
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}
          >
            {event.title}
          </MDTypography>
          <Box>
            {isEditing ? (
              <>
                <IconButton
                  onClick={handleSave}
                  color="success"
                  size="small"
                  sx={{ mr: 1 }}
                  disabled={isLoading}
                >
                  <Icon>check</Icon>
                </IconButton>
                <IconButton
                  onClick={() => setIsEditing(false)}
                  size="small"
                  sx={{ mr: 1 }}
                  disabled={isLoading}
                >
                  <Icon>close</Icon>
                </IconButton>
              </>
            ) : (
              <>
                <IconButton onClick={() => setIsEditing(true)} color="info" size="small" sx={{ mr: 1 }}>
                  <Icon>edit</Icon>
                </IconButton>
                <IconButton onClick={handleDeleteClick} color="error" size="small" sx={{ mr: 1 }}>
                  <DeleteIcon />
                </IconButton>
                <IconButton onClick={onClose} size="small">
                  <Icon>close</Icon>
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {renderField(
          "Type",
          event.extendedProps.type,
          <Select
            fullWidth
            name="type"
            value={editedData?.type || ""}
            onChange={(e) => {
              handleInputChange(e);
              setTimeErrors({ starttime: "", endtime: "" });
            }}
            size="small"
            sx={fieldStyle.editField}
          >
            <MenuItem value="Task">Task</MenuItem>
            <MenuItem value="Email">Email</MenuItem>
            <MenuItem value="Call">Call</MenuItem>
            <MenuItem value="Meeting">Meeting</MenuItem>
          </Select>
        )}

        {renderField(
          "Title",
          event.title,
          <TextField
            fullWidth
            required
            name="title"
            value={editedData?.title || ""}
            onChange={handleInputChange}
            size="small"
            sx={fieldStyle.editField}
            error={!editedData?.title}
            helperText={!editedData?.title ? "Title is required" : ""}
          />
        )}

        {renderField(
          "Status",
          event.extendedProps.status,
          <Select
            fullWidth
            name="status"
            value={editedData?.status || ""}
            onChange={handleInputChange}
            size="small"
            sx={fieldStyle.editField}
          >
            <MenuItem value="Scheduled">Scheduled</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        )}

        {renderField(
          "Description",
          event.extendedProps.description || "No description",
          <TextField
            fullWidth
            name="description"
            value={editedData?.description || ""}
            onChange={handleInputChange}
            multiline
            rows={4}
            size="small"
            sx={fieldStyle.editField}
          />
        )}

        {renderField(
          "Due Date",
          new Date(event.extendedProps.due_date).toLocaleString(),
            <TextField
                fullWidth
                name="due_date"
                type="datetime-local"
                value={editedData?.due_date?.slice(0, 16) || ""}
                onChange={(e) => {
                  handleInputChange(e);
                  setDueDateError(e.target.value ? "" : "Due date is required");
                }}
                error={!!dueDateError}
                helperText={dueDateError}
                size="small"
                sx={fieldStyle.editField}
                InputLabelProps={{ shrink: true }}
            />
        )}

        {(isEditing ? editedData?.type : event.extendedProps.type) === "Meeting" && (
          <>
            {renderField(
              "Start Time",
              event.extendedProps.starttime || "Not set",
                <TextField
                    fullWidth
                    name="starttime"
                    type="time"
                    value={editedData?.starttime || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      setTimeErrors((prev) => ({ ...prev, starttime: "", endtime: "" }));
                    }}
                    error={!!timeErrors.starttime}
                    helperText={timeErrors.starttime}
                    size="small"
                    sx={fieldStyle.editField}
                    InputLabelProps={{ shrink: true }}
                />
            )}

            {renderField(
              "End Time",
              event.extendedProps.endtime || "Not set",
                <TextField
                    fullWidth
                    name="endtime"
                    type="time"
                    value={editedData?.endtime || ""}
                    onChange={(e) => {
                      handleInputChange(e);
                      setTimeErrors((prev) => ({ ...prev, endtime: "" }));
                    }}
                    error={!!timeErrors.endtime}
                    helperText={timeErrors.endtime}
                    size="small"
                    sx={fieldStyle.editField}
                    InputLabelProps={{ shrink: true }}
                />
            )}
          </>
        )}

        {renderField(
          "Assigned To",
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: 'pointer',
              color: 'info.main',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => handleUserClick(event.extendedProps.assignee_id)}
          >
            <Avatar
              alt={event.extendedProps.assigned_to_name}
              src={event.extendedProps.assigned_to_image}
              sx={{ width: 20, height: 20, mr: 1 }}
            />
            <Typography
              variant="body2" // This makes the text smaller
              sx={{
                color: 'inherit',
                fontSize: '0.900rem', // You can also explicitly set the font size
                // fontWeight: 500,
              }}
            >
              {event.extendedProps.assigned_to_name}
            </Typography>
          </Box>,

          <Select
            fullWidth
            name="assignee_id"
            value={editedData?.assignee_id || ""}
            onChange={handleInputChange}
            size="small"
            sx={fieldStyle.editField}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.fullname}
              </MenuItem>
            ))}
          </Select>
        )}

        {isEditing ? (
          <Box mb={2}>
            <Typography sx={fieldStyle.label}>Related To</Typography>
            <Select
              fullWidth
              name="type"
              value={editedData?.related_to?.type || ""}
              onChange={handleRelatedToChange}
              size="small"
              sx={{ ...fieldStyle.editField, mb: 1 }}
            >
              {relatedToOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            {editedData?.related_to?.type && (
              <Autocomplete
                options={relatedEntities[editedData.related_to.type] || []}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  switch (editedData.related_to.type) {
                    case "account":
                      return option.name || "";
                    case "lead":
                      return `${option.first_name} ${option.last_name} - ${option.company || ""}`;
                    case "deal":
                      return `${option.name} - ${option.amount || ""}`;
                    case "contact":
                      return `${option.first_name} ${option.last_name} - ${option.email || ""}`;
                    case "case":
                      return `${option.case_number || ""} - ${option.subject || ""}`;
                    case "order":
                      return `${option.order_number || ""} - ${option.total_amount || ""}`;
                    case "quote":
                      return `${option.quote_name || ''} ${option.total_amount ? `- ${option.total_amount}` : ''}`.trim();
                    default:
                      return option.name || option.title || `ID: ${option.id}`;
                  }
                }}
                value={
                  relatedEntities[editedData.related_to.type]?.find(
                    (entity) => entity.id === editedData.related_to.id
                  ) || null
                }
                onChange={(_, newValue) => {
                  setEditedData((prev) => ({
                    ...prev,
                    related_to: {
                      ...prev.related_to,
                      id: newValue?.id || null
                    }
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={fieldStyle.editField} />
                )}
              />
            )}
          </Box>
        ) : (
          <Box mb={2}>
            <Typography sx={fieldStyle.label}>Related To</Typography>
            <Typography sx={fieldStyle.value}>
              <span style={{ fontWeight: "bold", textTransform: "capitalize" }}>
                {event.extendedProps.parent_table.replace(/s$/, "")}
              </span>
              :{" "}
              <Box
                component="span"
                onClick={handleEntityClick}
                sx={{
                  color: "info.main",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                {event.extendedProps.entity_name}
              </Box>
            </Typography>
          </Box>
        )}

        <Box mb={2}>
          <Typography sx={fieldStyle.label}>Created By</Typography>
          <Typography sx={fieldStyle.value}>
            <Link
              component="span"
              onClick={() => handleUserClick(event.extendedProps.created_by)}
              sx={{
                cursor: 'pointer',
                color: 'info.main'
              }}
            >
              {event.extendedProps.created_by_name}
            </Link>
            {` on ${formatDate(event.extendedProps.created_at)}`}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography sx={fieldStyle.label}>Last Updated By</Typography>
          <Typography sx={fieldStyle.value}>
            <Link
              component="span"
              onClick={() => handleUserClick(event.extendedProps.updated_by)}
              sx={{
                cursor: 'pointer',
                color: 'info.main'
              }}
            >
              {event.extendedProps.updated_by_name}
            </Link>
            {` on ${formatDate(event.extendedProps.updated_at)}`}
          </Typography>
        </Box>

        <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2
            }}
          >
            <Typography
              variant="h6"
              mb={2}
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#344767",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
              }}
            >
              Confirm Delete
            </Typography>
            <Typography
              variant="body1"
              mb={3}
              sx={{
                fontSize: "0.875rem",
                color: "#67748e",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
              }}
            >
              Are you sure you want to delete this task? This action cannot be undone.
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
                sx={{
                  textTransform: "none",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 500
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 500
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>

      <Modal open={updateConfirmOpen} onClose={() => setUpdateConfirmOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#344767",
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}
          >
            Confirm Update
          </Typography>
          <Typography
            variant="body1"
            mb={3}
            sx={{
              fontSize: "0.875rem",
              color: "#67748e",
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}
          >
            Are you sure you want to update this task?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <MDButton
              onClick={() => setUpdateConfirmOpen(false)}
              color="secondary"
              variant="text"
              sx={{
                textTransform: "none",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleConfirmUpdate}
              color="info"
              variant="gradient"
              disabled={isLoading}
              sx={{
                textTransform: "none",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500
              }}
            >
              {isLoading ? "Updating..." : "Update"}
            </MDButton>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const TASK_VIEW_WIDTH = 3; // Number of columns for task view (out of 12)
const ANIMATION_DURATION = 300; // Duration in milliseconds

const POPOVER_STYLES = {
  position: 'absolute !important',
  backgroundColor: '#ffffff !important',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1) !important',
  border: '1px solid rgba(0,0,0,0.05) !important',
  borderRadius: '12px !important',
  maxWidth: '400px !important',
  width: '100% !important',
  overflow: 'hidden !important',
  zIndex: 9999,
  opacity: '1 !important',
  '& .fc-popover-header': {
    backgroundColor: '#f8f9fa !important',
    padding: '12px 16px !important',
    borderBottom: '1px solid #eee !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
    '& .fc-popover-title': {
      margin: '0 !important',
      fontSize: '14px !important',
      fontWeight: '600 !important',
      color: '#344767 !important',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
      textTransform: 'none !important',
      letterSpacing: '0.3px !important'
    },
    '& .fc-popover-close': {
      color: '#666666 !important',
      opacity: '1 !important',
      fontSize: '16px !important',
      padding: '4px !important',
      cursor: 'pointer !important',
      background: 'transparent !important',
      border: 'none !important',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      width: '24px !important',
      height: '24px !important',
      borderRadius: '50% !important',
      transition: 'all 0.2s ease !important',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.04) !important',
        color: '#344767 !important'
      }
    }
  },
  '& .fc-popover-body': {
    padding: '12px 0 !important',
    maxHeight: '400px !important',
    overflowY: 'auto !important',
    '&::-webkit-scrollbar': {
      width: '6px !important'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f8f9fa !important'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ddd !important',
      borderRadius: '3px !important',
      '&:hover': {
        backgroundColor: '#ccc !important'
      }
    },
    '& .fc-daygrid-event-harness': {
      margin: '0 !important',
      '& .fc-event': {
        margin: '4px 8px !important',
        padding: '10px 12px !important',
        borderRadius: '8px !important',
        border: 'none !important',
        backgroundColor: '#f8f9fa !important',
        transition: 'all 0.2s ease !important',
        position: 'relative !important',
        display: 'flex !important',
        alignItems: 'center !important',
        gap: '8px !important',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '0',
          top: '0',
          bottom: '0',
          width: '4px',
          backgroundColor: 'currentColor',
          borderRadius: '4px 0 0 4px',
          opacity: '0.8'
        },
        '& .fc-event-main': {
          flex: '1 !important',
          '& .fc-event-title': {
            fontSize: '13px !important',
            fontWeight: '500 !important',
            color: '#344767 !important',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
            marginBottom: '4px !important'
          },
          '& .fc-event-time': {
            fontSize: '12px !important',
            color: '#67748e !important',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
            opacity: '0.8 !important'
          }
        },
        '&:hover': {
          backgroundColor: '#f1f3f9 !important',
          transform: 'translateY(-1px) !important',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05) !important'
        }
      }
    }
  }
};

const FilterContainer = styled(Card)(({ theme }) => ({
  padding: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
}));

const FilterGrid = styled(Grid)({
  alignItems: "center",
  gap: "16px",
});

function Tasks() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { user, axiosInstance } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [calendarWidth, setCalendarWidth] = useState(12);
  const [eventErrors, setEventErrors] = useState(EMPTY_EVENT_ERRORS);
  const modalContentRef = useRef(null);

  // New state variables for related entities and filters
  const [relatedEntities, setRelatedEntities] = useState({});
  const [filters, setFilters] = useState({
    parent_table: null,
    parent_table_id: null,
    owner_id: null
  });
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);

  // Add the showSnackbar function
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  useEffect(() => {
    fetchEvents({ user_id: user.id });
  }, [user.id]);

  useEffect(() => {
    fetchEvents({ ...filters });
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setShowOwnerFilter(user.role === "system_admin" || user.role === "manager");
  }, [user.role]);

  const handleFilterChange = (newFilters) => {
    if (newFilters.filter_status === null) {
      setFilters({
        parent_table: null,
        parent_table_id: null,
        owner_id: null
      });
    } else {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }
  };

  const getFilteredEvents = () => {
    return events.filter((event) => {
      if (filters.parent_table && event.extendedProps.parent_table !== `${filters.parent_table}s`) {
        return false;
      }
      if (filters.owner_id && event.extendedProps.assignee_id !== filters.owner_id) {
        return false;
      }
      return true;
    });
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(`/users/`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      showSnackbar("Failed to fetch users", "error");
    }
  };

  const fetchEvents = async (filters = {}) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      params.append("user_id", user.id);
      if (filters.parent_table) {
        params.append("parent_table", `${filters.parent_table}s`);
      }
      if (filters.owner_id) {
        params.append("user_id", filters.owner_id);
      }

      const response = await axiosInstance.get(`/activities/?${params.toString()}`);

      const formattedEvents = response.data.map((activity) => ({
        id: activity.id,
        title: activity.title,
        start: activity.due_date,
        end: activity.due_date,
        // For stacking behavior in week/day views, mark Tasks as all-day events.
        allDay: activity.type === "Task",
        backgroundColor: "#4B7BE5",
        borderColor: "#4B7BE5",
        textColor: "#F8F9FA",
        extendedProps: {
          type: activity.type,
          title: activity.title,
          status: activity.status,
          description: activity.description,
          due_date: activity.due_date,
          starttime: activity.starttime,
          endtime: activity.endtime,
          parent_table: activity.parent_table,
          parent_table_id: activity.parent_table_id,
          assignee_id: activity.assigned_to,
          created_at: activity.created_at,
          updated_at: activity.updated_at,
          created_by: activity.created_by,
          created_by_name: activity.created_by_name,
          updated_by: activity.updated_by,
          updated_by_name: activity.updated_by_name,
          assigned_to_name: activity.assigned_to_name,
          entity_name: activity.entity_name
        }
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      showSnackbar("Failed to fetch events", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // v2 header "New task": the same dialog as Create Task. Called above the
  // loading return (hooks cannot follow it); it waits for the page to load,
  // by which time handleCreateTask below is defined.
  useOpenCreateFromUrl(() => handleCreateTask(), { ready: !isLoading && Boolean(user?.id) });

  // UI v2 layout: page header with Create Task, the filters in a slim card,
  // then the same calendar. Read above the loading return (hooks).
  const isV2 = useTheme()?.pearlUi === "v2";
  const { app } = useActiveApp();

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          title="Loading Task Data"
          message="Please wait while we fetch the tasks information..."
        />
        
      </DashboardLayout>
    );
  }

  const handleEventClick = (info) => {
    if (!info || !info.event) return;
    setIsAnimating(true);
    setCalendarWidth(12 - TASK_VIEW_WIDTH);

    const formattedEvent = {
      id: info.event.id,
      title: info.event.title || info.event.extendedProps.title,
      type: info.event.extendedProps.type,
      start: info.event.extendedProps.due_date,
      notes: info.event.extendedProps.description,
      status: info.event.extendedProps.status,
      assignee_id: info.event.extendedProps.assignee_id,
      starttime: info.event.extendedProps.starttime,
      endtime: info.event.extendedProps.endtime,
      related_to: {
        type: info.event.extendedProps.parent_table?.replace(/s$/, ""),
        id: info.event.extendedProps.parent_table_id
      },
      originalEvent: info.event
    };

    setTimeout(() => {
      setSelectedEvent(formattedEvent);
      setIsModalOpen(false);
      setIsAnimating(false);
    }, ANIMATION_DURATION / 2);
  };

  const handleTaskViewClose = () => {
    setIsAnimating(true);
    setCalendarWidth(12);
    setTimeout(() => {
      setSelectedEvent(null);
      setIsAnimating(false);
    }, ANIMATION_DURATION / 2);
  };

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.dateStr);
    const options = { timeZone: "Asia/Kolkata" };
    const nowInIST = new Date(new Date().toLocaleString("en-US", options));

    clickedDate.setUTCHours(nowInIST.getHours(), nowInIST.getMinutes(), 0, 0);
    let endDate = new Date(clickedDate);
    endDate.setMinutes(endDate.getMinutes() + 30);

    const newEvent = {
      start: formatLocalDateTimeInput(clickedDate),
      end: formatLocalDateTimeInput(endDate),
      type: "Task",
      allDay: true, // Mark Task events as all-day for stacking
      assignee_id: user.id,
      related_to: { type: "", id: null },
      description: "",
      status: "Scheduled"
    };
    setSelectedEvent(newEvent);
    setIsNewEvent(true);
    setIsModalOpen(true);
  };

  const handleDeleteEvent = async () => {
    try {
      await axiosInstance.delete(`/activities/${selectedEvent.id}`);
      setEvents(events.filter((event) => event.id !== selectedEvent.id));
      handleCloseModal();
      showSnackbar("Activity deleted successfully");
      fetchEvents();
      // --- Log activity delete ---
      await frontendLogger.logDelete('Activity', {
        id: selectedEvent.id,
        action: 'delete',
        deleted_by: user.id,
        deleted_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error deleting activity:", error);
      showSnackbar("Failed to delete activity", "error");
    }
  };

  const handleCloseModal = () => {
    setEventErrors(EMPTY_EVENT_ERRORS);
    setIsModalOpen(false);
    setSelectedEvent(null);
    setIsNewEvent(false);
  };

  const validateEvent = () => {
    const errors = { ...EMPTY_EVENT_ERRORS };

    if (!selectedEvent.title?.trim()) errors.title = "Title is required";

    if (!selectedEvent.start) {
      errors.start = "Due date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (new Date(selectedEvent.start) < today) {
        errors.start = "Cannot create tasks for past dates";
      }
    }

    if (selectedEvent.type === "Meeting") {
      const start = (selectedEvent.starttime || "").slice(0, 5);
      const end = (selectedEvent.endtime || "").slice(0, 5);
      if (!start) errors.starttime = "Start time is required";
      if (!end) errors.endtime = "End time is required";
      if (start && end && end <= start) errors.endtime = "End time must be after start time";
    }

    setEventErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleSaveEvent = async () => {
    if (!validateEvent()) {
      scrollToFirstInvalid(modalContentRef.current);
      return;
    }

    setIsLoading(true);
    try {
      const pluralizeParentTable = (type) => {
        if (!type) return "";
        return `${type}s`;
      };

      const activityData = {
        type: selectedEvent.type,
        title: selectedEvent.title,
        due_date: toLocalDateTimeApi(selectedEvent.start),
        assigned_to: selectedEvent.assignee_id,
        parent_table: pluralizeParentTable(selectedEvent.related_to.type),
        parent_table_id: selectedEvent.related_to.id,
        description: selectedEvent.notes,
        status: selectedEvent.status || "Scheduled"
      };

      if (selectedEvent.type === "Meeting") {
        activityData.starttime = normalizeTimeToHHMMSS(selectedEvent.starttime);
        activityData.endtime = normalizeTimeToHHMMSS(selectedEvent.endtime);
        activityData.due_date = formatLocalDateInput(selectedEvent.start);
      }

      const response = isNewEvent
        ? await axiosInstance.post("/activities/", activityData)
        : await axiosInstance.put(`/activities/${selectedEvent.id}`, activityData);

      handleCloseModal();
      showSnackbar(`Activity ${isNewEvent ? "created" : "updated"} successfully`);
      fetchEvents(filters);

      // --- Log activity create/update ---
      if (isNewEvent) {
        await frontendLogger.logCreate('Activity', {
          id: response.data.id,
          action: 'create',
          title: response.data.title,
          assigned_to: response.data.assigned_to,
          due_date: response.data.due_date,
          created_by: user.id,
          created_at: response.data.created_at || new Date().toISOString(),
        });
      } else {
        await frontendLogger.logUpdate('Activity', {
          id: response.data.id,
          action: 'update',
          title: response.data.title,
          assigned_to: response.data.assigned_to,
          due_date: response.data.due_date,
          updated_by: user.id,
          updated_at: response.data.updated_at || new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error saving activity:", error);
      showSnackbar(`Failed to ${isNewEvent ? "create" : "update"} activity`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setEventErrors((prev) => {
      const next = { ...prev, [name]: "" };
      if (name === "type" || name === "starttime") next.endtime = "";
      if (name === "type") next.starttime = "";
      return next;
    });

    setSelectedEvent((prev) => {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        return { ...prev, [parent]: { ...prev[parent], [child]: value } };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchRelatedEntities = async (type) => {
    try {
      let response;
      if (type === "quote") {
        response = await axiosInstance.get('/quotations/'); // Fetch quotations
      } else {
        response = await axiosInstance.get(`/${type}s/`); // Fetch other entities
      }
      
      setRelatedEntities((prev) => ({
        ...prev,
        [type]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching ${type}s:`, error);
      showSnackbar(`Failed to fetch ${type}s`, "error");
    }
  };

  const handleRelatedToChange = async (e) => {
    const { name, value } = e.target;
    if (name === "type") {
      setSelectedEvent((prev) => ({
        ...prev,
        related_to: { type: value, id: "" }
      }));
      if (value && !relatedEntities[value]) {
        await fetchRelatedEntities(value);
      }
    } else if (name === "id") {
      setSelectedEvent((prev) => ({
        ...prev,
        related_to: { ...prev.related_to, id: value }
      }));
    }
  };

  const handleCreateTask = () => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 2);

    const newEvent = {
      title: "",
      start: formatLocalDateTimeInput(futureDate),
      type: "Task",
      allDay: true, // Mark new Task as all-day
      assignee_id: user.id,
      related_to: { type: "", id: "" },
      notes: "",
      status: "Scheduled"
    };
    setSelectedEvent(newEvent);
    setIsNewEvent(true);
    setIsModalOpen(true);
  };

  const FilterBar = ({ filters, onFilterChange, dense }) => {
    const [parentEntities, setParentEntities] = useState({});
    const { axiosInstance } = useAuth();

    const fetchParentEntities = async (type) => {
      try {
        const response = await axiosInstance.get(`/${type}s/`);
        setParentEntities((prev) => ({
          ...prev,
          [type]: response.data
        }));
      } catch (error) {
        console.error(`Error fetching ${type}s:`, error);
      }
    };

    useEffect(() => {
      if (filters.parent_table) {
        fetchParentEntities(filters.parent_table);
      }
    }, [filters.parent_table]);

    return (
      <Box
        sx={{ mb: dense ? 0 : 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Parent Object</InputLabel>
          <Select
            value={filters.parent_table || ""}
            onChange={(e) => {
              onFilterChange({ parent_table: e.target.value });
            }}
            label="Parent Entity"

          >
            {relatedToOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showOwnerFilter && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Owner</InputLabel>
            <Select
              value={filters.owner_id || ""}
              onChange={(e) => {
                onFilterChange({ owner_id: e.target.value });
              }}
              label="Owner"

            >
              <MenuItem value="">Self</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.fullname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button variant="gradient" size="small" onClick={() => onFilterChange({ filter_status: null })}>
          Clear Filters
        </Button>
      </Box>
    );
  };

  // Shared by the classic and v2 layouts: the same calendar and task panel.
  const calendarGrid = (
    <Grid container spacing={3}>
      <Grid
        item
        xs={12}
        md={calendarWidth}
        sx={{
          transition: `all ${ANIMATION_DURATION}ms ease-in-out`,
          opacity: isAnimating ? 0.5 : 1,
        }}
      >
        <MDBox mb={3}>
          <Calendar
            header={isV2 ? {} : { title: "Calendar" }}
            events={getFilteredEvents()}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            height="calc(100vh - 310px)"
            contentHeight="calc(100vh - 310px)"
            slotDuration="00:30:00"
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            headerToolbar={{
              left: isV2 ? "today prev,next" : "prev,next today createTask",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay"
            }}
            customButtons={{
              createTask: {
                text: "Create Task",
                click: handleCreateTask
              }
            }}
            initialView="dayGridMonth"
            dayMaxEvents={3}
            dayMaxEventRows={3}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false
            }}
            eventContent={(eventInfo) => {
              let backgroundColor = '#4B7BE5';
              let textDecoration = 'none';
              let textColor = '#F8F9FA';

              if (eventInfo.event.extendedProps.status === 'Completed') {
                backgroundColor = '#66BB6A';
              } else if (eventInfo.event.extendedProps.status === 'Cancelled') {
                backgroundColor = '#9E9E9E';
                textDecoration = 'line-through';
              }
              if (isV2) {
                // v2: tinted chips with dark ink text (WCAG AA).
                const status = eventInfo.event.extendedProps.status;
                const tone = status === 'Completed' ? v2Tones.success : status === 'Cancelled' ? v2Tones.neutral : v2Tones.info;
                backgroundColor = tone.bg;
                textColor = tone.fg;
              }

              const startTime = eventInfo.event.extendedProps.type === 'Meeting'
                ? formatTimeForDisplay(eventInfo.event.extendedProps.starttime)
                : new Date(eventInfo.event.extendedProps.due_date).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });

              return (
                <div style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                  lineHeight: '1.2',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor,
                  borderRadius: '3px',
                  margin: '0',
                  padding: '0 6px',
                  boxShadow: isV2 ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    width: '100%',
                    height: '100%'
                  }}>
                    <span style={{
                      color: textColor,
                      fontSize: '0.7rem',
                      fontWeight: 400,
                      minWidth: 'fit-content',
                      marginRight: '4px'
                    }}>
                      {startTime}
                    </span>
                    <span style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: isV2 ? 'inherit' : '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: textColor,
                      textDecoration
                    }}>
                      {eventInfo.event.title || eventInfo.event.extendedProps.type}
                    </span>
                  </div>
                </div>
              );
            }}
            dayCellDidMount={(arg) => {
              arg.el.style.padding = '0';
              arg.el.style.overflow = 'hidden';
              arg.el.style.cursor = 'pointer';
              arg.el.style.transition = 'background-color 0.2s ease';
              arg.el.addEventListener('mouseenter', () => {
                arg.el.style.backgroundColor = 'rgba(0,0,0,0.02)';
              });
              arg.el.addEventListener('mouseleave', () => {
                arg.el.style.backgroundColor = '';
              });
              arg.el.addEventListener('mousedown', () => {
                arg.el.style.backgroundColor = 'rgba(0,0,0,0.05)';
              });
              arg.el.addEventListener('mouseup', () => {
                arg.el.style.backgroundColor = 'rgba(0,0,0,0.02)';
              });
            }}
            eventDidMount={(arg) => {
              arg.el.style.margin = '1px 2px';
              arg.el.style.padding = '0';
              arg.el.style.backgroundColor = 'transparent';
              arg.el.style.border = 'none';
              arg.el.style.cursor = 'pointer';
              arg.el.addEventListener('mouseenter', () => {
                arg.el.style.transform = 'translateY(-1px)';
                arg.el.style.transition = 'transform 0.2s ease';
              });
              arg.el.addEventListener('mouseleave', () => {
                arg.el.style.transform = 'translateY(0)';
              });
            }}
            styleProps={{
              ".fc": {
                "--fc-small-font-size": "0.75rem",
                "--fc-page-bg-color": "#fff",
                "--fc-neutral-bg-color": "#fff",
                fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
                position: "relative"
              },
              ".fc-day-grid-container": {
                height: "auto !important"
              },
              ".fc-daygrid-day-frame": {
                padding: "2px !important",
                cursor: "pointer"
              },
              ".fc-daygrid-day-events": {
                margin: "0 !important",
                padding: "0 2px !important"
              },
              ".fc-event-main": {
                padding: "0 !important"
              },
              ".fc-daygrid-event-harness": {
                margin: "4px 0 !important"
              },
              ".fc-toolbar-title": {
                fontSize: "1.25rem !important",
                fontWeight: "500 !important",
                color: "#344767 !important"
              },
              ".fc-button": {
                textTransform: "capitalize !important",
                fontWeight: "400 !important"
              },
              ".fc-day:hover": {
                backgroundColor: "rgba(0,0,0,0.02)",
                cursor: "pointer"
              },
              ".fc-scrollgrid": {
                borderRadius: "8px !important",
                overflow: "hidden !important",
                border: "1px solid #ddd !important"
              },
              ".fc-theme-standard td": {
                border: "1px solid #eee !important"
              },
              ".fc-col-header-cell": {
                backgroundColor: "#f8f9fa !important",
                borderBottom: "2px solid #ddd !important",
                padding: "8px 0 !important"
              },
              ".fc-col-header-cell-cushion": {
                padding: "4px 8px !important",
                color: "#344767 !important",
                fontWeight: "600 !important"
              },
              ".fc-popover": {
                ...POPOVER_STYLES,
                "&:hover, &:focus, &:active": {
                  opacity: "1 !important"
                }
              },
              ".fc-more-popover": {
                ".fc-popover-body": {
                  maxHeight: "400px !important",
                  overflowY: "auto !important",
                  "&::-webkit-scrollbar": {
                    width: "6px !important"
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f8f8f8 !important"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c8c8c8 !important",
                    borderRadius: "3px !important",
                    "&:hover": {
                      background: "#a8a8a8 !important"
                    }
                  }
                },
                ".fc-daygrid-event": {
                  display: "flex !important",
                  alignItems: "center !important",
                  gap: "8px !important",
                  fontSize: "13px !important",
                  fontWeight: "400 !important",
                  color: "#333333 !important",
                  backgroundColor: "transparent !important",
                  borderLeft: "3px solid currentColor !important",
                  margin: "2px 0 !important",
                  padding: "8px 12px !important",
                  cursor: "pointer !important",
                  transition: "background-color 0.2s ease !important",
                  "&:hover": {
                    backgroundColor: "#f8f8f8 !important"
                  }
                },
                ".fc-daygrid-event-dot": {
                  display: "none !important"
                }
              },
              ".fc-popover *": {
                opacity: "1 !important"
              },
              ".fc-popover-header, .fc-popover-body": {
                opacity: "1 !important"
              }
            }}
            views={{
              dayGridMonth: {
                dayMaxEventRows: 3,
                dayMaxEvents: 3
              },
              timeGridWeek: {
                dayMaxEvents: 3,
                allDayMaxEventRows: 3,
                slotminHeight: 20,
                scrollTime: "00:00:00"
              },
              timeGridDay: {
                dayMaxEvents: 3,
                allDayMaxEventRows: 3,
                slotminHeight: 20,
                scrollTime: "00:00:00"
              }
            }}
          />
        </MDBox>
      </Grid>

      {selectedEvent && !isModalOpen && (
        <Grid
          item
          xs={12}
          md={TASK_VIEW_WIDTH}
          sx={{
            transition: `all ${ANIMATION_DURATION}ms ease-in-out`,
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating ? 'translateX(50px)' : 'translateX(0)',
            height: 'calc(100vh - 310px)',
          }}
        >
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              boxShadow: isV2 ? t.shadow : "0 2px 4px rgba(0,0,0,0.1)",
              borderRadius: isV2 ? "10px" : "0.75rem",
              border: isV2 ? `1px solid ${t.border}` : "1px solid rgba(0,0,0,0.05)",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                height: "100%",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px"
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.05)"
                }
              }}
            >
              <TaskView
                event={selectedEvent.originalEvent}
                onDelete={handleDeleteEvent}
                onClose={handleTaskViewClose}
                onNotify={showSnackbar}
                onUpdate={async (updatedData) => {
                  setIsLoading(true);
                  await fetchEvents(filters);
                  setSelectedEvent({
                    ...selectedEvent,
                    originalEvent: {
                      id: updatedData.id,
                      title: updatedData.title,
                      extendedProps: {
                        type: updatedData.type,
                        title: updatedData.title,
                        status: updatedData.status,
                        description: updatedData.description,
                        due_date: updatedData.due_date,
                        starttime: updatedData.starttime,
                        endtime: updatedData.endtime,
                        assignee_id: updatedData.assigned_to,
                        assigned_to_name: updatedData.assigned_to_name,
                        parent_table: updatedData.parent_table,
                        parent_table_id: updatedData.parent_table_id,
                        entity_name: updatedData.entity_name,
                        updated_at: updatedData.updated_at,
                        updated_by: updatedData.updated_by,
                        updated_by_name: updatedData.updated_by_name,
                        created_at: selectedEvent.originalEvent.extendedProps.created_at,
                        created_by: selectedEvent.originalEvent.extendedProps.created_by,
                        created_by_name: selectedEvent.originalEvent.extendedProps.created_by_name,
                      }
                    }
                  });
                  setIsLoading(false);
                }}
              />
            </Box>
          </Card>
        </Grid>
      )}
    </Grid>
  );
  const filteredEvents = getFilteredEvents();
  const openCount = filteredEvents.filter((ev) => !["Completed", "Cancelled"].includes(ev.extendedProps.status)).length;
  const v2Main = (
    <MDBox>
      <PageHeader
        title={app?.labels?.tasks || "Tasks"}
        summary={`${filteredEvents.length} ${filteredEvents.length === 1 ? "task" : "tasks"} · ${openCount} open`}
        actions={
          <MDButton variant="gradient" color="info" onClick={handleCreateTask}>
            <Icon>add</Icon>&nbsp;Create Task
          </MDButton>
        }
      />
      <Card sx={{ px: 2, py: 1.5, mb: 2.5 }}>
        <FilterBar filters={filters} onFilterChange={handleFilterChange} dense />
      </Card>
      {calendarGrid}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* GlobalStyles overrides */}
      <GlobalStyles
        styles={{
          // Lock the height of timeGrid (week/day) views and enable vertical scroll
          ".fc .fc-timegrid .fc-scroller": {
            maxHeight: "calc(100vh - 310px) !important",
            overflowY: "auto !important"
          },
          // Hour text style
          ".fc .fc-timegrid-slot-label, .fc .fc-timegrid-slot-label-cushion": {
            fontSize: "0.75em !important",
            color: "grey !important"
          },
          // All-day event text in timeGrid views to match hour text
          ".fc .fc-timegrid-view .fc-daygrid-event, .fc .fc-timegrid-view .fc-daygrid-event-dot": {
            fontSize: "0.75em !important",
            color: "grey !important"
          },
          // Override the all-day header (extreme left column) in timeGrid view
          ".fc .fc-timegrid .fc-col-header-cell-cushion": {
            fontSize: "0.75em !important",
            color: "grey !important",
            fontWeight: "normal !important"
          }
        }}
      />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <FilterContainer>
          <FilterGrid container>
            <Grid item xs={12}>
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                sx={{
                  '& .MuiFormControl-root': {
                    height: '45px',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      height: '45px',
                      borderRadius: '8px',
                    }
                  }
                }}
              />
            </Grid>
          </FilterGrid>
        </FilterContainer>

        {calendarGrid}
      </MDBox>
      )}
      
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 3,
              py: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <MDTypography
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              {isNewEvent ? "Create New Task" : "Edit Task"}
            </MDTypography>
            {!isNewEvent && (
              <IconButton
                aria-label="delete"
                onClick={handleDeleteEvent}
                sx={{ color: "#FFFFFF" }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          {/* Content */}
          <Box ref={modalContentRef} sx={{ p: 3, maxHeight: "90vh", flex: "1 1 auto", minHeight: 0 }}>
            {selectedEvent && (
              <Box component="form" noValidate autoComplete="off">
                <FormControl fullWidth margin="normal">
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    name="type"
                    value={selectedEvent.type || ""}
                    onChange={handleInputChange}
                    label="Task Type"

                  >
                    <MenuItem value="Task">Task</MenuItem>
                    <MenuItem value="Email">Email</MenuItem>
                    <MenuItem value="Call">Call</MenuItem>
                    <MenuItem value="Meeting">Meeting</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  required
                  label="Title"
                  name="title"
                  value={selectedEvent.title || ""}
                  onChange={handleInputChange}
                  margin="normal"
                  error={!!eventErrors.title}
                  helperText={eventErrors.title}
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="notes"
                  value={selectedEvent.notes || ""}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={4}
                />

                <TextField
                  fullWidth
                  label="Due Date"
                  name="start"
                  type={selectedEvent.type === "Meeting" ? "date" : "datetime-local"}
                  value={
                    selectedEvent.type === "Meeting"
                      ? selectedEvent.start?.split("T")[0]
                      : selectedEvent.start
                  }
                  onChange={handleInputChange}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: selectedEvent.type === "Meeting"
                      ? formatLocalDateInput(new Date())
                      : formatLocalDateTimeInput(new Date())
                  }}
                  error={!!eventErrors.start}
                  helperText={eventErrors.start}
                />

                {selectedEvent.type === "Meeting" && (
                  <>
                    <TextField
                      fullWidth
                      label="Start Time"
                      name="starttime"
                      type="time"
                      value={normalizeTimeToHHMM(selectedEvent.starttime)}
                      onChange={handleInputChange}
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      error={!!eventErrors.starttime}
                      helperText={eventErrors.starttime}
                    />
                    <TextField
                      fullWidth
                      label="End Time"
                      name="endtime"
                      type="time"
                      value={normalizeTimeToHHMM(selectedEvent.endtime)}
                      onChange={handleInputChange}
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      error={!!eventErrors.endtime}
                      helperText={eventErrors.endtime}
                    />
                  </>
                )}

                <FormControl fullWidth margin="normal">
                  <InputLabel>Owner</InputLabel>
                  <Select
                    name="assignee_id"
                    value={selectedEvent.assignee_id || ""}
                    onChange={handleInputChange}
                    label="Owner"

                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Related To</InputLabel>
                  <Select
                    name="type"
                    value={selectedEvent.related_to?.type || ""}
                    onChange={handleRelatedToChange}
                    label="Related To"

                  >
                    {relatedToOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedEvent.related_to?.type && (
                  <Autocomplete
                    options={relatedEntities[selectedEvent.related_to.type] || []}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      switch (selectedEvent.related_to.type) {
                        case "account":
                          return option.name || "";
                        case "lead":
                          return `${option.first_name || ''} ${option.last_name || ''} ${option.company ? `- ${option.company}` : ''}`.trim();
                        case "deal":
                          return `${option.name || ''} ${option.amount ? `- ${option.amount}` : ''}`.trim();
                        case "contact":
                          return `${option.first_name || ''} ${option.last_name || ''} ${option.email ? `- ${option.email}` : ''}`.trim();
                        case "case":
                          return `${option.case_number || ''} ${option.subject ? `- ${option.subject}` : ''}`.trim();
                        case "order":
                          return `${option.order_number || ''} ${option.total_amount ? `- ${option.total_amount}` : ''}`.trim();
                        case "quote":
                          return `${option.quote_name || ''} ${option.total_amount ? `- ${option.total_amount}` : ''}`.trim();
                        default:
                          return option.name || option.title || "";
                      }
                    }}
                    value={
                      relatedEntities[selectedEvent.related_to.type]?.find(
                        (entity) => entity.id === selectedEvent.related_to.id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setSelectedEvent((prev) => ({
                        ...prev,
                        related_to: {
                          ...prev.related_to,
                          id: newValue?.id || null
                        }
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={`Search ${selectedEvent.related_to.type}`} margin="normal" fullWidth />
                    )}
                    ListboxProps={{
                      style: { maxHeight: "200px" }
                    }}
                    sx={{
                      "& .MuiAutocomplete-input": {
                        height: "45px"
                      }
                    }}
                  />
                )}

                {/* Actions */}
                <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
                  <MDButton
                    onClick={handleCloseModal}
                    variant="outlined"
                    color="secondary"
                    sx={{ marginRight: 2 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    onClick={handleSaveEvent}
                    variant="gradient"
                    color="info"
                  >
                    {isNewEvent ? "Create Task" : "Save Changes"}
                  </MDButton>
                </MDBox>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Tasks;
